import React, { Component } from 'react';
import SideMenu from '../admin/SideMenu';
import { app, appDatabasePrimaryFunctions} from '../../base';
import TopMenu from '../admin/TopBar';
import '../../styles/css/AdminMain.css';
import {findFirstAndLastName, convertTimeStampToHumanReadable} from '../utils/HelpfulFunction';
import DatePicker from '../utils/DatePicker';

// Plain-English definitions shown when the ⓘ icon next to a metric is clicked.
const METRIC_INFO = {
    activeUsers: {
        title: "Active Users",
        text: "Unique people who used the app at least once during the selected date range (Google Analytics \"active users\"). Each person is counted once, no matter how many times they opened the app. The iOS/Android line splits the same count by platform."
    },
    newUsers: {
        title: "New Users",
        text: "People who opened the app for the very first time during the selected date range. This is not the same as store downloads — someone can download the app but never open it, or reinstall without counting as new again."
    },
    sessions: {
        title: "Sessions",
        text: "The number of separate visits to the app in the selected date range — one person opening the app five times counts as five sessions. \"Engaged\" sessions are the subset that lasted longer than 10 seconds, viewed 2+ screens, or triggered a conversion event."
    },
    avgEngagement: {
        title: "Avg Engagement / User",
        text: "The average total time each active user spent with the app in the foreground over the selected date range (total engagement time ÷ active users). The smaller number below it is the average length of a single session."
    },
    dau: {
        title: "DAU — Daily Active Users",
        text: "Unique users who were active in the last 1 day, measured as of the end date shown. This is a rolling snapshot, so the start date doesn't affect it."
    },
    wau: {
        title: "WAU — Weekly Active Users",
        text: "Unique users who were active at least once in the last 7 days, measured as of the end date shown. A rolling snapshot — the start date doesn't affect it."
    },
    mau: {
        title: "MAU — Monthly Active Users",
        text: "Unique users who were active at least once in the last 30 days, measured as of the end date shown. A rolling snapshot — the start date doesn't affect it."
    },
    stickiness: {
        title: "Stickiness (DAU/MAU)",
        text: "Daily active users divided by monthly active users — the share of your monthly audience that shows up on a given day. Higher means people come back more often; sports apps typically spike on game days."
    },
    dailyTrend: {
        title: "Active Users — daily",
        text: "The number of unique users active on each individual day across the selected date range. The same person active on multiple days appears in each of those days."
    },
    iosDownloads: {
        title: "iOS Downloads",
        text: "First-time downloads from the App Store (Apple \"App Units\" from App Store Connect sales reports). Excludes app updates, re-downloads and restores. \"All-time\" is the sum of every year we have data for. Store reports lag by a few days; numbers refresh nightly."
    },
    androidDownloads: {
        title: "Android Downloads",
        text: "Unique users who installed the app from Google Play (Google's \"Daily User Installs\"), summed over the year. Excludes updates, and counts each user once per day regardless of devices. Store reports lag by a few days; numbers refresh nightly."
    },
};

class AdminHome extends Component {
    constructor(props) {
        super(props);
        const todaysDateObject = new Date();
        todaysDateObject.setDate(todaysDateObject.getDate()-30);
        const analyticsStartObject = new Date();
        analyticsStartObject.setDate(analyticsStartObject.getDate()-30);
        this.state = {
            games: 0,
            users: 0,
            itemsPerPage: 10,
            itemsToShow: 10,
            uniqueGamesUsers: 0,
            loading: false,
            players: 0,
            gameUsers: 0,
            prizesWon: 0,
            checkIns: 0,
            lastGame: {},
            uniqueGameUsers: false,
            gamesToShowList: [],
            searchStart: todaysDateObject,
            searchEnd: new Date(),
            activeTab: 'dashboard',
            analytics: null,
            analyticsLoading: false,
            analyticsConfigured: true,
            analyticsError: null,
            analyticsStart: analyticsStartObject,
            analyticsEnd: new Date(),
            downloads: null,
            downloadsLoading: false,
            downloadsError: null,
            downloadYear: new Date().getFullYear(),
            infoMetric: null
        };
        this.handleChange = this.handleChange.bind(this);
    }

    handleChange (evt) {
        const target = evt.target;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        this.setState({
            [target.name]: value
        });
    }

    componentDidMount(){
        this.getGames(this.state.searchStart.getTime());
        this.getUsers(this.state.searchStart.getTime());
        this.getAppAnalytics();
        this.getAppDownloads();
        document.addEventListener('keydown', this.handleInfoKeydown);
    }

    componentWillUnmount(){
        document.removeEventListener('keydown', this.handleInfoKeydown);
    }

    async getAppDownloads(){
        this.setState({ downloadsLoading: true, downloadsError: null });
        try {
            const currentUser = app.auth().currentUser;
            if(!currentUser){
                this.setState({ downloadsLoading: false });
                return;
            }
            const idToken = await currentUser.getIdToken();
            let apiUrl = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/api/appDownloads`;
            if(process.env.NODE_ENV === "development"){
                apiUrl = `http://localhost:5001/${process.env.REACT_APP_FIREBASE_PROJECT_ID}/us-central1/api/appDownloads`;
            }
            const response = await fetch(apiUrl, { headers: { 'Authorization': `Bearer ${idToken}` } });
            const data = await response.json();
            if(!response.ok){
                this.setState({ downloadsLoading: false, downloadsError: data.message || data.error || 'Failed to load downloads', downloads: null });
                return;
            }
            this.setState({ downloadsLoading: false, downloads: data, downloadsError: null });
        } catch (e) {
            this.setState({ downloadsLoading: false, downloadsError: e.message, downloads: null });
        }
    }

    renderDownloadsPlatform(label, platform, configured){
        if(!configured){
            return (
                <div className="card card-styles" style={{padding:16, backgroundColor:"#f3f3f3"}}>
                    <span style={{color:"#666"}}>{label}: not configured for this environment yet.</span>
                </div>
            );
        }
        const data = platform || {};
        const byYear = data.byYear || {};
        const years = Object.keys(byYear).sort();
        // A real fetch error that produced nothing — show "couldn't load" rather than a misleading 0.
        if(data.hadError && !(data.allTime > 0) && years.length === 0){
            return (
                <div className="card card-styles" style={{padding:16, backgroundColor:"var(--warn-wash)"}}>
                    <span style={{color:"var(--warn)", fontSize:12.5}}>{label}: couldn't load downloads yet (access pending / will retry on next sync).</span>
                </div>
            );
        }
        return (
            <div className="admin-statcard">
                <div className="row" style={{alignItems:"baseline"}}>
                    <div className="col-md-5">
                        <p className="admin-eyebrow">{label} &mdash; all-time downloads</p>
                        <p className="admin-statcard-value">{this.formatNumber(data.allTime || 0)}</p>
                    </div>
                    <div className="col-md-7">
                        <div className="row">
                            {years.length === 0 &&
                                <span className="admin-statcard-sub">No yearly data yet.</span>
                            }
                            {years.map((y) => (
                                <div key={y} className="col" style={{textAlign:"center"}}>
                                    <p className="admin-statcard-value" style={{fontSize:20}}>{byYear[y] === null ? '\u2014' : this.formatNumber(byYear[y])}</p>
                                    <p className="admin-statcard-sub" style={{fontFamily:"var(--admin-mono)", fontSize:11}}>{y}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Small clickable ⓘ that opens the definition of a metric (keys of METRIC_INFO).
    renderInfoIcon(key, color){
        return (
            <button type="button" className="admin-info-btn" aria-label={`What is ${key}?`}
                    style={color ? {color} : undefined}
                    onClick={(e)=>{ e.stopPropagation(); this.setState({infoMetric: key}); }}>
                <span className="fa fa-info-circle" aria-hidden="true"/>
            </button>
        );
    }

    renderInfoModal(){
        const info = METRIC_INFO[this.state.infoMetric];
        if(!info) return null;
        return (
            <div onClick={()=>this.setState({infoMetric: null})}
                 style={{position:"fixed", top:0, left:0, right:0, bottom:0, backgroundColor:"rgba(0,0,0,0.5)", zIndex:1050,
                     display:"flex", alignItems:"center", justifyContent:"center"}}>
                <div onClick={(e)=>e.stopPropagation()} className="card card-styles"
                     role="dialog" aria-modal="true" aria-label={info.title}
                     style={{maxWidth:440, width:"90%", padding:20, backgroundColor:"white"}}>
                    <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8}}>
                        <h5 style={{margin:0, color:"black"}}>{info.title}</h5>
                        <button type="button" className="admin-modal-close" aria-label="Close"
                                onClick={()=>this.setState({infoMetric: null})}>&times;</button>
                    </div>
                    <p style={{color:"var(--slate)", fontSize:14, margin:0, lineHeight:1.5}}>{info.text}</p>
                </div>
            </div>
        );
    }

    // The info dialog could only be dismissed by clicking, which is no use
    // to anyone on a keyboard.
    handleInfoKeydown = (e) => {
        if (e.key === 'Escape' && this.state.infoMetric) {
            this.setState({ infoMetric: null });
        }
    };

    availableDownloadYears(downloads){
        // Union of every year present across iOS + Android, plus the current year,
        // sorted descending so the current year is first (and the default).
        const yearsSet = new Set();
        [downloads && downloads.ios, downloads && downloads.android].forEach(p => {
            if(p && p.byYear){
                Object.keys(p.byYear).forEach(y => yearsSet.add(parseInt(y, 10)));
            }
        });
        yearsSet.add(new Date().getFullYear());
        return [...yearsSet].filter(y => !isNaN(y)).sort((a, b) => b - a);
    }

    renderDownloadsYear(label, platform, configured, year){
        if(!configured){
            return (
                <div className="card card-styles" style={{padding:16, backgroundColor:"#f3f3f3"}}>
                    <span style={{color:"#666"}}>{label}: not configured for this environment yet.</span>
                </div>
            );
        }
        const data = platform || {};
        const byYear = data.byYear || {};
        const years = Object.keys(byYear);
        // A real fetch error that produced nothing — show "couldn't load" rather than a misleading 0.
        if(data.hadError && !(data.allTime > 0) && years.length === 0){
            return (
                <div className="card card-styles" style={{padding:16, backgroundColor:"var(--warn-wash)"}}>
                    <span style={{color:"var(--warn)", fontSize:12.5}}>{label}: couldn't load downloads yet (access pending / will retry on next sync).</span>
                </div>
            );
        }
        const raw = byYear[year];
        const yearValue = raw === null ? null : (raw || 0);   // null = a failed year (show —), missing = 0
        return (
            <div className="admin-statcard">
                <p className="admin-eyebrow">{label} &mdash; {year} downloads{this.renderInfoIcon(label === 'iOS' ? 'iosDownloads' : 'androidDownloads')}</p>
                <p className="admin-statcard-value">{yearValue === null ? '\u2014' : this.formatNumber(yearValue)}</p>
                <p className="admin-statcard-sub">{this.formatNumber(data.allTime || 0)} all-time</p>
            </div>
        );
    }

    async getAppAnalytics(){
        this.setState({ analyticsLoading: true, analyticsError: null });
        try {
            const currentUser = app.auth().currentUser;
            if(!currentUser){
                this.setState({ analyticsLoading: false });
                return;
            }
            const idToken = await currentUser.getIdToken();
            let apiUrl = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/api/appAnalytics`;
            if(process.env.NODE_ENV === "development"){
                apiUrl = `http://localhost:5001/${process.env.REACT_APP_FIREBASE_PROJECT_ID}/us-central1/api/appAnalytics`;
            }
            // Send the picked CALENDAR dates (local Y-M-D), not epoch ms. Epoch ms get
            // formatted to a UTC date server-side, which shifts the window a day forward
            // in the evening (Pacific), mislabeling the range and landing end on a partial day.
            const toLocalYmd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
            let start = this.state.analyticsStart;
            let end = this.state.analyticsEnd;
            if(start && typeof start === "object") start = toLocalYmd(start);
            if(end && typeof end === "object") end = toLocalYmd(end);
            const params = new URLSearchParams();
            if(start) params.append('start', start);
            if(end) params.append('end', end);
            const response = await fetch(`${apiUrl}?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${idToken}` }
            });
            const data = await response.json();
            if(!response.ok){
                this.setState({
                    analyticsLoading: false,
                    analyticsConfigured: true,
                    analyticsError: data.message || data.error || 'Failed to load analytics',
                    analytics: null
                });
                return;
            }
            this.setState({
                analyticsLoading: false,
                analytics: data.configured === false ? null : data,
                analyticsConfigured: data.configured !== false,
                analyticsError: null
            });
        } catch (e) {
            this.setState({ analyticsLoading: false, analyticsError: e.message, analytics: null });
        }
    }

    formatNumber(n){
        return (n || 0).toLocaleString();
    }

    formatDuration(totalSeconds){
        const s = Math.round(totalSeconds || 0);
        if(s < 60) return `${s}s`;
        const m = Math.floor(s / 60);
        if(m < 60) return `${m}m ${s % 60}s`;
        const h = Math.floor(m / 60);
        return `${h}h ${m % 60}m`;
    }

    // A fully-zeroed analytics object, used so the Analytics cards still render
    // (all 0s) when the live data can't be loaded, instead of an error banner.
    zeroAnalytics(){
        return {
            totals: { activeUsers:0, newUsers:0, sessions:0, engagedSessions:0, avgEngagementPerUser:0, averageSessionDuration:0 },
            byPlatform: [],
            rolling: { dau:0, wau:0, mau:0, stickiness:0 },
            trend: [],
            dateRange: null,
        };
    }

    platformValue(byPlatform, name, metric){
        const row = (byPlatform || []).find(p => (p.platform || '').toLowerCase() === name.toLowerCase());
        return row ? (row[metric] || 0) : 0;
    }

    renderTrendChart(trend, dateRange){
        // GA only returns rows for days that had activity, so a sparse range
        // collapses to a few points. Zero-fill every day in the selected range
        // so the chart spans the full timeframe with quiet days at the baseline.
        const valueByDate = {};
        (trend || []).forEach(t => { valueByDate[t.date] = t.activeUsers || 0; });
        let series = [];
        if(dateRange && dateRange.startDate && dateRange.endDate){
            const start = new Date(dateRange.startDate + "T00:00:00");
            const end = new Date(dateRange.endDate + "T00:00:00");
            for(let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)){
                const key = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
                series.push({ date: key, activeUsers: valueByDate[key] || 0 });
            }
        } else {
            series = (trend || []).map(t => ({ date: t.date, activeUsers: t.activeUsers || 0 }));
        }
        if(series.length === 0){
            return null;
        }
        const width = 900;
        const height = 200;
        const pad = 32;
        const max = Math.max(...series.map(s => s.activeUsers), 1);
        const stepX = (width - pad * 2) / Math.max(series.length - 1, 1);
        const coords = series.map((s, i) => {
            const x = pad + i * stepX;
            const y = height - pad - (s.activeUsers / max) * (height - pad * 2);
            return { x, y, value: s.activeUsers };
        });
        const linePoints = coords.map(c => `${c.x},${c.y}`).join(' ');
        const areaPoints = `${pad},${height - pad} ${linePoints} ${pad + (series.length - 1) * stepX},${height - pad}`;
        const formatGaDate = (d) => {
            if(!d || d.length < 8) return d;
            return `${parseInt(d.slice(4, 6), 10)}/${parseInt(d.slice(6, 8), 10)}`;
        };
        const labelIndexes = [0, Math.floor((series.length - 1) / 2), series.length - 1];
        return (
            <svg viewBox={`0 0 ${width} ${height}`} style={{width: "100%", height: "auto"}} preserveAspectRatio="none">
                <polyline points={areaPoints} fill="rgba(13,110,253,0.25)" stroke="none"/>
                <polyline points={linePoints} fill="none" stroke="var(--ink)" strokeWidth="2"/>
                {coords.filter(c => c.value > 0).map((c, i) => (
                    <circle key={i} cx={c.x} cy={c.y} r="2.5" fill="var(--ink)"/>
                ))}
                <text x={pad} y={pad - 12} fill="#9aa0a6" fontSize="13">peak {this.formatNumber(max)}</text>
                {labelIndexes.map((idx, i) => (
                    <text key={i} x={pad + idx * stepX} y={height - 8} fill="#9aa0a6" fontSize="12" textAnchor="middle">
                        {formatGaDate(series[idx].date)}
                    </text>
                ))}
            </svg>
        );
    }

    async getGames(start = 0, end){
        const vm = this;
        let queryString = appDatabasePrimaryFunctions.ref('games').orderByChild('date').startAt(start);
        let currentGame = null;
        let currentGameObject = await appDatabasePrimaryFunctions.ref('currentGame').once('value').then(function(snapshot){
            let gameTemp = snapshot.val();
            if(gameTemp && gameTemp.timeStamp > start){
                return gameTemp;
            } else {
                return null;
            }
        })
        if(end){
            end = end + 86400000;
            queryString = queryString.endAt(end);
            if(currentGameObject && currentGameObject.date > end){
                currentGameObject = null;
            }
        }
        currentGame = currentGameObject;
        let currentGamePrizes = 0;
        let currentGameUsers = 0;
        let gamesCount = 0;
        let gameCheckIns = 0;
        if(currentGame){
            // currentGamePrizes = currentGamePrizes + await appDatabasePrimaryFunctions.ref('emailsSent').once('value').then(function(snapshot){
            //     return snapshot.numChildren()
            // })
            // currentGameUsers = currentGameUsers +  await appDatabasePrimaryFunctions.ref("userGameHistory").orderByChild(currentGame.id).equalTo(currentGame.id).once('value').then(function (snapshot){
            //     return snapshot.numChildren()
            // })
            // const answersCount = await appDatabasePrimaryFunctions.ref("userAnswers").once('value').then(function(snapshot){
            //     return snapshot.numChildren()
            // })
            gamesCount++;
            currentGame.prizesWon = currentGamePrizes;
            currentGame.users = currentGameUsers;
            currentGame.currentGame = true;
        }
        queryString.once('value', function (snapshot) {
            let prizesCount = currentGamePrizes;
            let gameUsersCount = currentGameUsers;
            let gamesArray = [];
            let index = 0;
            snapshot.forEach(function (data) {
                let gamesValue = data.val();
                index++;
                prizesCount += gamesValue.couponRedeems || 0;
                gameUsersCount += gamesValue.usersThatPlayed || gamesValue.users || 0;
                gameCheckIns += gamesValue.numberCheckedIn || gamesValue.checkIns || 0;
                if(snapshot.numChildren() - vm.state.itemsPerPage < index){
                    gamesArray.unshift(gamesValue)
                }
            })
            if(currentGame){
                gamesArray.unshift(currentGame)
            }
            vm.setState({
                loading: false,
                games: gamesCount + snapshot.numChildren(),
                prizesWon: prizesCount,
                gameUsers: gameUsersCount,
                checkIns: gameCheckIns
                // gamesToShowList: gamesArray
            })
        }).catch((e)=> {
            if(e.message.indexOf("permission_denied") !== -1){
                this.props.noPermissionResponse();
            }
        });
    }

    async getUsers(start=0, end, returnUsers=false){
        const vm = this;
        let queryString = appDatabasePrimaryFunctions.ref('users').orderByChild('signUpTime');
        if(start){
            queryString = queryString.startAt(start);
        }
        if(end){
            queryString = queryString.endAt(end + 86400000);
        }
        if(returnUsers){
            return await queryString.once('value').then(function(snapshot) {
                if(snapshot.exists()){
                    return snapshot.val();
                } else {
                    return {};
                }
            })
        } else {
            queryString.once('value', function(snapshot){
                vm.setState({
                    loading: false,
                    users: snapshot.numChildren()
                })
            });
        }
    }

    async getUserData(){
        this.setState({
            loading: true
        })
        let searchStart = this.state.searchStart || 0;
        let searchEnd = this.state.searchEnd;
        if(typeof searchStart === "object"){
            searchStart = searchStart.getTime()
        }
        if(searchEnd && typeof searchEnd === "object"){
            searchEnd = searchEnd.getTime()
        }
        let csv = process.env.REACT_APP_CLEAN_CLIENT_NAME || process.env.REACT_APP_FIREBASE_PROJECT_ID;
        const date_got = new Date((new Date().getTime() - new Date().getTimezoneOffset()*60*1000)).toISOString().split("T")[0];
        csv+=",App\n,\n"
        csv+="Date Data Downloaded,"+date_got+"\n";
        csv+="From,"+(searchStart?convertTimeStampToHumanReadable(searchStart):"")+"\n";
        csv+="To,"+(searchEnd?convertTimeStampToHumanReadable(searchEnd):"")+"\n,\n";
        csv += 'Email,First Name,Last Name,Phone Number,Zip Code,Address,Birthday,Opt-In,Signed Up\n';
        const vm = this;
        let timeFrameUsers = await this.getUsers(searchStart, searchEnd, true);
        for(let timeFrameUserIndex in timeFrameUsers){
            let user = timeFrameUsers[timeFrameUserIndex];
            let {firstName, secondName} = findFirstAndLastName(user.name);
            csv += (user.email || user.uid || "") + ",";
            // csv += (cleanCSVText(firstName) || "") + ",";
            // csv += (cleanCSVText(secondName) || "") + ",";
            // csv += (user.phoneNumber || "") + ",";
            // csv += (cleanCSVText(user.zipCode) || "") + ",";
            // csv += (cleanCSVText(user.address) || "") + ",";
            // csv += (user.birthday || "") + ",";
            // csv += (user.optIn || "") + ",";
            // csv += (user.signUpTime?convertTimeStampToHumanReadable(user.signUpTime):"")
            csv += "\n";
        }
        const hiddenElement = document.createElement('a');
        hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
        hiddenElement.target = '_blank';
        let cleanString = (searchStart?"_"+convertTimeStampToHumanReadable(searchStart):"")+(searchEnd?"_"+convertTimeStampToHumanReadable(searchEnd):"");
        cleanString = "accounts_made" + cleanString.replace(/[|&;$%@"<>()+,]/g, "").toLowerCase()+".csv"
        hiddenElement.download = cleanString;
        hiddenElement.click();
        vm.setState({
            loading:false
        })
    }

    async downloadIntenseGameData(game){
        this.setState({
            loading: true
        })
        let csv = process.env.REACT_APP_CLEAN_CLIENT_NAME + ',' + 'App\n""\n';
        let date_downloaded = new Date((new Date().getTime() - new Date().getTimezoneOffset()*60*1000)).toISOString().split("T")[0];
        const gameAnswers = await appDatabasePrimaryFunctions.ref(`userAnswersHistory/${game.id}`).once('value').then(function(snapshot){
            if(snapshot.exists()){
                return snapshot.val();
            } else {
                return []
            }
        });
        let totalPrizes = 0;
        for(const userIndex in gameAnswers){
            const userInfo = gameAnswers[userIndex];
            if(userInfo.rewardSent){
                totalPrizes++;
            }
        }
        csv+= 'Date Data Downloaded,'+date_downloaded+'\n';
        csv+= 'Game Name,'+game.gameName+'\n';
        // csv+= 'Start Time,'+(game.scheduleInfo?convertTimeStampToHumanReadable(game.scheduleInfo.performAt):"")+'\n';
        // csv+= 'End Time,'+(game.scheduleInfo?convertTimeStampToHumanReadable(game.scheduleInfo.endAt):"")+'\n';
        csv+= 'Rewards Won,'+totalPrizes.toString()+'\n""\n';
        csv+= 'Email,First Name,Last Name,Phone Number,Zip Code,Address,Birthday,Opt-In,Won?,Reward,Code,Answer Time\n';

        const usersData = await appDatabasePrimaryFunctions.ref('users').once('value').then(function(usersSnapshot){
            return usersSnapshot.val();
        })

        for(const userIndex in gameAnswers){
            const userInfo = gameAnswers[userIndex];
            const correct = userInfo.rewardSent ? "YES": "NO";
            const moreUserData = usersData[userInfo.uid];
            let {firstName, secondName} = findFirstAndLastName(moreUserData.name);
            csv+= (userInfo.email || userInfo.uid || "")+'\n'
            // +','+(cleanCSVText(firstName) || "")
            // +','+(cleanCSVText(secondName) || "")
            // +','+(moreUserData.phoneNumber || "")
            // +','+(cleanCSVText(moreUserData.zipCode) || "")
            // +","+(cleanCSVText(moreUserData.address) || "")
            // +','+(moreUserData.birthday||"")
            // +','+(moreUserData.optIn||"")
            // +','+(correct||"")+','+(cleanCSVText(userInfo.rewardSent)||"")
            // +','+(cleanCSVText(userInfo.code)||"")
            // +','+convertTimeStampToHumanReadable(parseInt(userInfo.timeStamp))+'\n'
        }
        let hiddenElement = document.createElement('a');
        hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
        hiddenElement.target = '_blank';
        let cleanString = game.gameName.replace(/[|&;$%@"<>()+,. ]/g, "").toLowerCase();
        hiddenElement.download = "game_data_"+cleanString+".csv";
        hiddenElement.click();
        this.setState({
            loading: false
        })
    }

    searchData(){
        let searchStart = this.state.searchStart;
        let searchEnd = this.state.searchEnd;
        if(searchStart){
            searchStart = searchStart.getTime();
        }
        if(searchEnd){
            searchEnd = searchEnd.getTime();
        }
        this.setState({
            loading: true,
            itemsPerPage: this.state.itemsPerPage
        }, ()=>{

        })
        this.getGames(searchStart,searchEnd)
        this.getUsers(searchStart,searchEnd)
    }

    searchAnalytics(){
        // Analytics has its own date range, independent of the dashboard's.
        this.getAppAnalytics();
    }

    onNextPage() {
        const vm = this;
        const newAmountToShow = this.state.itemsToShow + this.state.itemsPerPage;
        let searchStart = this.state.searchStart;
        let searchEnd = this.state.searchEnd;
        if(searchStart){
            searchStart = searchStart.getTime();
        }
        if(searchEnd){
            searchEnd = searchEnd.getTime();
        }
        let queryString = appDatabasePrimaryFunctions.ref('gamesList').orderByChild('timeStamp').limitToLast(newAmountToShow).startAt(searchStart);
        if (searchEnd) {
            queryString = queryString.endAt(searchEnd + 86400000)
        }
        queryString.once('value', function (snapshot) {
            let gamesArray = [];
            snapshot.forEach(function (data) {
                gamesArray.unshift(data.val())
            })
            vm.setState({
                itemsToShow: newAmountToShow,
                gamesToShowList: gamesArray
            });
        })
    }

    render() {
        const totalUsers = this.state.users || 0;
        const gameUsers = this.state.gameUsers || 0;
        const prizesWon = this.state.prizesWon || 0;
        const checkIns = this.state.checkIns || 0;
        const playerPickEnabled = process.env.REACT_APP_HAS_PLAYER_PICK === "true";
        const checkInEnabled = process.env.REACT_APP_HAS_CHECK_IN === "true";
        const { analyticsLoading, analyticsConfigured } = this.state;
        // Fall back to zeroed cards when the live pull failed (e.g. local emulator
        // missing the Analytics scope) rather than showing an error banner.
        const analytics = this.state.analytics || (analyticsConfigured ? this.zeroAnalytics() : null);
        const { downloads, downloadsLoading, downloadsError } = this.state;
        const activeTab = this.state.activeTab;
        const downloadYear = this.state.downloadYear;
        const downloadYears = this.availableDownloadYears(downloads);
        const tabButton = (key, labelText) => (
            <button type="button" onClick={() => this.setState({ activeTab: key })}
                style={{background:"none", border:"none",
                    borderBottom: activeTab === key ? "3px solid var(--ink)" : "3px solid transparent",
                    color: activeTab === key ? "var(--ink)" : "var(--slate)", fontWeight:600, fontSize:16,
                    padding:"8px 16px", cursor:"pointer"}}>
                {labelText}
            </button>
        );
        return (
          <div className="admin-wrapper">
            <div className="loading-screen" style={{display: this.state.loading ? 'block' : 'none' }}/>
            {this.renderInfoModal()}
            <SideMenu/>
            <TopMenu/>
            <div className="admin-main-panel">
                {/* the version was floating alone at the top of the screen with
                    no title above it and nothing to relate it to */}
                <div className="admin-page-head">
                    {/* the build sits opposite the eyebrow rather than trailing
                        the subtitle, where it wrapped onto its own line */}
                    <div className="admin-head-row">
                        <p className="admin-eyebrow">Overview</p>
                        <span className="admin-version">v{process.env.REACT_APP_VERSION}</span>
                    </div>
                    <p className="admin-header-text">Dashboard</p>
                    <p className="admin-page-sub">
                        How your app is being used. Pick a date range to change what the numbers cover.
                    </p>
                </div>

                <div style={{borderBottom:"1px solid var(--line)", margin:"14px 24px 12px", display:"flex", gap:4}}>
                    {tabButton('dashboard', 'Dashboard')}
                    {tabButton('analytics', 'Analytics')}
                    {tabButton('downloads', 'Downloads')}
                </div>

                {activeTab === 'dashboard' && (
                <>
                <form className="form-horizontal" style={{marginLeft: 20, marginBottom:10}}>
                    <div className="row g-3 align-items-center">
                        <div className="col-auto">
                            <DatePicker selected={this.state.searchStart} onChange={date => this.setState({searchStart: date || this.state.searchStart})}/>
                        </div>

                        <div className="col-auto">
                            <DatePicker isClearable selected={this.state.searchEnd} onChange={date => this.setState({searchEnd: date})}/>
                        </div>
                        <div className="col-auto">
                            <button className="btn btn-primary btn-admin" type="button" onClick={()=>this.searchData()}>Search</button>
                        </div>
                    </div>
                </form>

                <p className="admin-eyebrow" style={{margin:"20px 24px 0"}}>Account activity</p>
                <div className="admin-statgrid">
                    <div className="admin-statcard">
                        <div style={{display:"flex", alignItems:"flex-start", gap:10}}>
                            <div style={{flex:1}}>
                                <p className="admin-eyebrow">Accounts created</p>
                                <p className="admin-statcard-value">{totalUsers}</p>
                            </div>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={()=> this.getUserData()}>
                                Export
                            </button>
                        </div>
                    </div>
                    {playerPickEnabled &&
                        <div className="admin-statcard">
                            <p className="admin-eyebrow">Game Users</p>
                            <p className="admin-statcard-value">{gameUsers}</p>
                        </div>
                    }
                    {/*<div className="card card-styles text-xs-center" style={{backgroundColor:"black"}}>*/}
                    {/*    <div className="card-body">*/}
                    {/*        <blockquote className="card-bodyquote" style={{margin:0}}>*/}
                    {/*            <div className="row">*/}
                    {/*                <div className="col-md-8">*/}
                    {/*                    <p style={{color:"white", fontSize:50, margin:0}}>{prizesWon}</p>*/}
                    {/*                    <span style={{color:"#f8f8ff", fontSize:20}}>Prizes Won</span>*/}
                    {/*                </div>*/}
                    {/*            </div>*/}
                    {/*        </blockquote>*/}
                    {/*    </div>*/}
                    {/*</div>*/}
                    {checkInEnabled &&
                        <div className="admin-statcard">
                            <p className="admin-eyebrow">Check Ins</p>
                            <p className="admin-statcard-value">{checkIns}</p>
                        </div>
                    }
                </div>
                </>
                )}

                {activeTab === 'analytics' && (
                <>
                <form className="form-horizontal" style={{marginLeft: 20, marginBottom:10}}>
                    <div className="row g-3 align-items-center">
                        <div className="col-auto">
                            <DatePicker selected={this.state.analyticsStart} onChange={date => this.setState({analyticsStart: date || this.state.analyticsStart})}/>
                        </div>
                        <div className="col-auto">
                            <DatePicker isClearable selected={this.state.analyticsEnd} onChange={date => this.setState({analyticsEnd: date})}/>
                        </div>
                        <div className="col-auto">
                            <button className="btn btn-primary btn-admin" type="button" onClick={()=>this.searchAnalytics()}>Search</button>
                        </div>
                    </div>
                </form>

                {/* ===== App Analytics (Google Analytics) ===== */}
                <div style={{margin: "20px 20px 0 20px"}}>
                    <h4 style={{color:"black", marginBottom: 2}}>App Analytics</h4>
                    <p style={{color:"#666", fontSize:13, marginBottom: 12}}>
                        Google Analytics · iOS &amp; Android
                        {analytics && analytics.dateRange ? ` · ${analytics.dateRange.startDate} → ${analytics.dateRange.endDate}` : ''}
                    </p>

                    {analyticsLoading &&
                        <p style={{color:"#666"}}>Loading analytics…</p>
                    }

                    {!analyticsLoading && !analyticsConfigured &&
                        <div className="card card-styles" style={{padding:16, backgroundColor:"var(--warn-wash)", marginBottom: 10}}>
                            <span style={{color:"var(--warn)", fontSize:12.5}}>Google Analytics isn't configured for this environment yet (no GA4 property id set).</span>
                        </div>
                    }

                    {!analyticsLoading && analytics &&
                        <>
                            <div className="admin-statgrid">
                                <div className="admin-statcard">
                                        <p className="admin-eyebrow">Active Users{this.renderInfoIcon('activeUsers')}</p>
                                        <p className="admin-statcard-value">{this.formatNumber(analytics.totals.activeUsers)}</p>
                                        <p className="admin-statcard-sub">iOS {this.formatNumber(this.platformValue(analytics.byPlatform,'iOS','activeUsers'))} · Android {this.formatNumber(this.platformValue(analytics.byPlatform,'Android','activeUsers'))}</p>
                                    </div>
                                <div className="admin-statcard">
                                        <p className="admin-eyebrow">New Users{this.renderInfoIcon('newUsers')}</p>
                                        <p className="admin-statcard-value">{this.formatNumber(analytics.totals.newUsers)}</p>
                                        <p className="admin-statcard-sub">iOS {this.formatNumber(this.platformValue(analytics.byPlatform,'iOS','newUsers'))} · Android {this.formatNumber(this.platformValue(analytics.byPlatform,'Android','newUsers'))}</p>
                                    </div>
                                <div className="admin-statcard">
                                        <p className="admin-eyebrow">Sessions{this.renderInfoIcon('sessions')}</p>
                                        <p className="admin-statcard-value">{this.formatNumber(analytics.totals.sessions)}</p>
                                        <p className="admin-statcard-sub">{this.formatNumber(analytics.totals.engagedSessions)} engaged</p>
                                    </div>
                                <div className="admin-statcard">
                                        <p className="admin-eyebrow">Avg Engagement / User{this.renderInfoIcon('avgEngagement')}</p>
                                        <p className="admin-statcard-value">{this.formatDuration(analytics.totals.avgEngagementPerUser)}</p>
                                        <p className="admin-statcard-sub">{this.formatDuration(analytics.totals.averageSessionDuration)} avg session</p>
                                    </div>
                            </div>

                            <div className="admin-eyebrow" style={{margin:"18px 0 6px"}}>
                                Rolling active users — as of {analytics.dateRange ? analytics.dateRange.endDate : 'end date'} (not affected by the start date)
                            </div>
                            <div className="admin-statgrid" style={{marginTop:0}}>
                                <div className="admin-statcard">
                                        <p className="admin-eyebrow">DAU{this.renderInfoIcon('dau')}</p>
                                        <p className="admin-statcard-value" style={{fontSize:24}}>{this.formatNumber(analytics.rolling.dau)}</p>
                                    </div>
                                <div className="admin-statcard">
                                        <p className="admin-eyebrow">WAU{this.renderInfoIcon('wau')}</p>
                                        <p className="admin-statcard-value" style={{fontSize:24}}>{this.formatNumber(analytics.rolling.wau)}</p>
                                    </div>
                                <div className="admin-statcard">
                                        <p className="admin-eyebrow">MAU{this.renderInfoIcon('mau')}</p>
                                        <p className="admin-statcard-value" style={{fontSize:24}}>{this.formatNumber(analytics.rolling.mau)}</p>
                                    </div>
                                <div className="admin-statcard">
                                        <p className="admin-eyebrow">Stickiness (DAU/MAU){this.renderInfoIcon('stickiness')}</p>
                                        <p className="admin-statcard-value" style={{fontSize:24}}>{(analytics.rolling.stickiness * 100).toFixed(1)}%</p>
                                    </div>
                            </div>

                            {analytics.trend && analytics.trend.length > 0 &&
                                <div className="admin-statcard" style={{marginTop:14}}>
                                    <p className="admin-eyebrow" style={{marginBottom:10}}>Active users &mdash; daily{this.renderInfoIcon('dailyTrend')}</p>
                                    {this.renderTrendChart(analytics.trend, analytics.dateRange)}
                                </div>
                            }
                        </>
                    }
                </div>
                </>
                )}

                {activeTab === 'downloads' && (
                <>
                {/* ===== App Store Downloads (all-time + per year) ===== */}
                <div style={{margin: "20px 20px 0 20px"}}>
                    <h4 style={{color:"black", marginBottom: 2}}>Downloads</h4>
                    <p style={{color:"#666", fontSize:13, marginBottom: 12}}>
                        App Store Connect &amp; Google Play · all-time and per year
                        {downloads && downloads.lastUpdated ? ` · updated ${convertTimeStampToHumanReadable(downloads.lastUpdated)}` : ''}
                    </p>

                    {downloadsLoading &&
                        <p style={{color:"#666"}}>Loading downloads…</p>
                    }

                    {!downloadsLoading && downloadsError &&
                        <div className="card card-styles" style={{padding:16, backgroundColor:"#f8d7da", marginBottom: 10}}>
                            <span style={{color:"#842029"}}>Couldn't load downloads: {downloadsError}</span>
                        </div>
                    }

                    {!downloadsLoading && downloads &&
                        <>
                            {!downloads.lastUpdated && (downloads.iosConfigured || downloads.androidConfigured) &&
                                <div className="card card-styles" style={{padding:12, backgroundColor:"var(--warn-wash)", marginBottom: 10}}>
                                    <span style={{color:"var(--warn)", fontSize:12.5}}>Credentials set — waiting on the first nightly sync (or hit refresh).</span>
                                </div>
                            }
                            <div style={{marginBottom:12}}>
                                <label style={{color:"#666", fontSize:13, marginRight:8, fontWeight:600}}>Year</label>
                                <select value={downloadYear}
                                    onChange={(e)=> this.setState({downloadYear: parseInt(e.target.value, 10)})}
                                    style={{padding:"6px 10px", fontSize:15, borderRadius:6, border:"1px solid #ccc"}}>
                                    {downloadYears.map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="admin-grid-container" style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10}}>
                                {this.renderDownloadsYear('iOS', downloads.ios, downloads.iosConfigured, downloadYear)}
                                {this.renderDownloadsYear('Android', downloads.android, downloads.androidConfigured, downloadYear)}
                            </div>
                        </>
                    }
                </div>
                </>
                )}

                {activeTab === 'dashboard' &&
                <div style={{margin:20}}>
                    {this.state.gamesToShowList && this.state.gamesToShowList.length > 0 &&
                    <>
                        {
                            this.state.gamesToShowList.map(function (item, index) {
                                return (
                                    <div key={index} className="card card-styles text-xs-center"
                                         style={{marginBottom: 10}}>
                                        <div className="card-body"
                                             style={{boxShadow: "rgba(0, 0, 0, 0.2) 0px 4px 8px 0px"}}>
                                            <div className="row" style={{alignItems: 'center'}}>
                                                <div className="col-md-2">
                                                    <span style={{color: 'black', fontSize: 14}}>{convertTimeStampToHumanReadable(item.timeStamp)}</span>
                                                </div>
                                                <div className="col-md-3" style={{fontSize: 30}}>
                                                    {item.gameName}
                                                </div>
                                                <div className="col-md-3" style={{textAlign: 'right'}}>
                                                    <span style={{fontSize: 25}}>{item.users || 0}</span>
                                                    <span style={{color: 'black', fontSize: 15, marginRight: 15}}>Played</span>
                                                    <span style={{fontSize: 25}}>{item.prizesWon || 0}</span>
                                                    <span style={{color: 'black', fontSize: 15}}>Rewards Won</span>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="row" style={{fontSize: 20, justifyContent: 'center'}}>
                                                        {item.prizesWon !== null && !item.currentGame &&
                                                        <button onClick={() => this.downloadIntenseGameData(item)} className="export-button-styles btn btn-primary btn-lg download-button mobile-hide">
                                                            <span className="fa fa-arrow-circle-down"/> Download Game Data
                                                        </button>
                                                        }
                                                        {item.currentGame &&
                                                        <button onClick={() => window.location.href='/setupgame'} className="export-button-styles btn btn-primary btn-lg download-button mobile-hide">
                                                            Go To Current Game
                                                        </button>
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            }, this)
                        }
                        {this.state.games > this.state.gamesToShowList.length &&
                            <button className="btn btn-primary" onClick={()=>this.onNextPage()}>
                                More
                            </button>
                        }
                    </>
                    }
                </div>
                }
            </div>
         </div>
        );
    }
}

export default AdminHome
